import * as filestack from "filestack-js";
import { postJSON } from "api/base";
import Constants from "const/constants";
import { s3upload } from "util/s3-utils";
import { mediainfoAnalyse } from "util/file-utils";
import { publicRuntimeConfig } from "util/env-utils";
import type { Upload } from "@aws-sdk/lib-storage";

type StoreLocation = {
    location: string;
    path: string;
    container: string;
    access: string; //TODO: probably better type here
};

class UploadService {
    organizationId: string;
    storeLocation: StoreLocation;
    filestackOptions: {
        maxSize: number;
        fromSources: string[];
        storeTo: StoreLocation;
    };
    filestack: filestack.Client;

    constructor(organizationId: string) {
        this.organizationId = organizationId;
        this.storeLocation = {
            location: `s3`,
            path: this.S3_PATH,
            container: this.STORE_NAME,
            access: `public`
        };
        this.filestackOptions = {
            maxSize: 20 * 1024 * 1024 * 1024,
            fromSources: [
                `url`,
                `dropbox`,
                `googledrive`,
                `gmail`,
                `onedrive`,
                `clouddrive`,
                `webcam`
            ],
            storeTo: this.storeLocation
        };
        this.filestack = filestack.init(publicRuntimeConfig.FILESTACK_API_KEY);
    }

    uploads: Record<string, Upload> = {};

    ENDPOINT = {
        VALIDATE_FILE_METADATA: `v1/validation/media-json`,
        VALIDATE_REMOTE_FILE: `v1/validation/media-url`
    };

    get S3_PATH() {
        return `uploads/tmp/or/${this.organizationId}/`;
    }

    get STORE_NAME() {
        return publicRuntimeConfig.s3BucketName;
    }

    /**
     * @summary - Uploads a given File
     *
     * @param file
     * @param onProgress File upload progress callback
     */
    async uploadFile(
        file: File,
        onProgress: (progress: number) => void,
        id?: string // weird, sometimes we don't use an id
    ) {
        const { response, upload } = await s3upload({ file, onProgress });
        this.uploads[id] = upload;
        return response;
    }

    /**
     * @summary - Aborts the upload of a given file
     *
     * @param id
     */
    stopUpload(id: string) {
        this.uploads[id].abort();
        delete this.uploads[id];
    }

    /**
     * @summary - Uploads a file at a given URL
     *
     * @param {String} url
     * @param {String} context
     *
     * @returns S3 path to asset
     */
    async uploadFileFromURL(
        url: string
    ): Promise<{ url: string; mimetype: string }> {
        //@ts-ignore: missing types
        const { key, mimetype } = await this.filestack.storeURL(
            url,
            this.storeLocation
        );
        return { url: `${Constants.CDN_URL}/${key}`, mimetype };
    }

    /**
     * @summary - Validates a blob against a certain context
     *
     * @param file
     * @param context
     */
    async localFileIsValid(
        file: Blob,
        context: string
    ): Promise<{ metadata: unknown; isValid: boolean; errors: unknown[] }> {
        //TODO: better type for metadata and errors
        const metadata = await mediainfoAnalyse(file);
        const payload = { context, mediaJson: {} };

        metadata.media.track.forEach((track) => {
            payload.mediaJson[track[`@type`].toLowerCase()] = track;
        });

        const {
            data: { valid, invalidReasons }
        } = await postJSON(this.ENDPOINT.VALIDATE_FILE_METADATA, payload);

        return {
            metadata,
            isValid: valid,
            errors: invalidReasons
        };
    }

    /**
     * @summary - Validates a file at a remote location via the validate media endpoint
     *
     * @param url
     * @param context Validation context
     *
     * @returns Object containing properties indiciating whether a file is valid and whether it needs revalidation
     */
    async remoteFileIsValid(url: string, context: string) {
        const {
            data: { revalidate, valid, invalidReasons }
        } = await postJSON(this.ENDPOINT.VALIDATE_REMOTE_FILE, {
            context,
            mediaUrl: url
        });

        return {
            errors: invalidReasons,
            isValid: valid,
            revalidate
        };
    }

    openFilestack(extraOptions: filestack.PickerOptions) {
        const instance = this.filestack.picker({
            ...this.filestackOptions,
            ...extraOptions
        });

        instance.open();

        return instance;
    }
}

export default UploadService;
